import React, { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { HomePage } from '@/components/landingPage/HomePage';
import useWidth from "@/hooks/useWidth";
import HeadMeta from '@/components/HeadMeta';
import { I18nextProvider, useTranslation } from 'react-i18next';
import i18next from '@/i18n';
import { LANDING_SECTIONS } from '@/components/landingPage/landingLocales';
import type { GetStaticProps } from 'next';
import { fetchCourseCatalog } from '@/utils/serverCourseCatalog';
import type { LandingStats } from '@/components/landingPage/HomePage';
import usePublicSession from '@/hooks/usePublicSession';
import { initializeReferrerFromURL, isReferrerApp } from '@/utils/referrer';

type LandingI18n = { locale: string; messages: Record<string, unknown> };

function usePublicAppReferrer() {
  const [isAppReferrer, setIsAppReferrer] = useState(false);
  useEffect(() => {
    initializeReferrerFromURL();
    const update = () => setIsAppReferrer(isReferrerApp());
    update();
    window.addEventListener('storage', update);
    return () => window.removeEventListener('storage', update);
  }, []);
  return isAppReferrer;
}

const DesktopClassroomHome = dynamic(() => import('@/pagesDesktop/Classroom/Home'), { ssr: false });
const MobileClassroomHome = dynamic(() => import('@/pagesMobile/Classroom/Home'), { ssr: false });
const ThemeCard = dynamic(() => import('@/components/classroom/ThemeCard'), { ssr: false });
const AppRuntimeProviders = dynamic(() => import('@/components/providers/AppRuntimeProviders'), { ssr: false });

function HomeContent({ stats, i18n }: { stats: LandingStats | null; i18n?: LandingI18n }) {
  // Server-rendered translation per URL (/fa, /de …) so every language is indexable.
  const landingI18n = React.useMemo(() => {
    const locale = i18n?.locale || 'en';
    if (locale === 'en') return i18next;
    const instance = i18next.cloneInstance({ lng: locale, initAsync: false });
    if (i18n && locale !== 'en') instance.addResourceBundle(locale, 'translation', i18n.messages, true, false);
    void instance.changeLanguage(locale);
    return instance;
  }, [i18n]);
  const { t } = useTranslation();
  const { session } = usePublicSession();
  const width = useWidth();
  const router = useRouter();
  const isAppReferrer = usePublicAppReferrer();

  if (session?.user?.accessToken) {
    return (
      <AppRuntimeProviders session={session}>
        <HeadMeta title={t('header.classroom')} />
        <ThemeCard />
        {width < 0 ? null : width >= 1024
          ? <DesktopClassroomHome props={{}} />
          : <MobileClassroomHome props={{}} />}
      </AppRuntimeProviders>
    );
  }

  if (isAppReferrer) {
    void router.push('/auth/login');
    return null;
  }

  return <>
    <I18nextProvider i18n={landingI18n}>
      <HomePage className="" stats={stats} locale={i18n?.locale || 'en'} />
    </I18nextProvider>
  </>
}

export default function Home(props: { stats: LandingStats | null; i18n?: LandingI18n }) {
  return <HomeContent {...props} />;
}

// Live catalog numbers for the landing page, refreshed hourly without slowing the page.
export const getStaticProps: GetStaticProps = async ({ locale = 'en' }) => {
  let i18n: LandingI18n = { locale: 'en', messages: {} };
  if (locale !== 'en') {
    try {
      const all = (await import(`../locales/${locale}/main.json`)).default as Record<string, unknown>;
      i18n = { locale, messages: Object.fromEntries(LANDING_SECTIONS.filter(k => k in all).map(k => [k, all[k]])) };
    } catch { /* unknown locale: English */ }
  }
  try {
    const catalog = await fetchCourseCatalog();
    const courses: any[] = Array.isArray(catalog?.courses) ? catalog.courses : [];
    const lower = (list: any[]) => new Set(list.map(v => String(v).toLowerCase()));
    const stats: LandingStats = {
      courses: Number(catalog?.count) || courses.length,
      lessons: courses.reduce((n, c) => n + (Number(c.lesson_count) || 0), 0),
      languages: new Set([...lower(catalog?.subjects || []), ...lower(catalog?.teaching_languages || [])]).size,
    };
    return { props: { stats, i18n }, revalidate: 3600 };
  } catch {
    return { props: { stats: null, i18n }, revalidate: 300 };
  }
};
